/**
 * Trump Job Approval MRP (Multilevel Regression and Poststratification) Frontend
 * 
 * This is the main React application that provides an interactive dashboard for
 * exploring Trump job approval data through multiple visualization types.
 * 
 * Key Features:
 * - Interactive US map showing state-level approval ratings
 * - Comprehensive demographic filtering system
 * - Multiple visualization modes (map, table, charts, heatmap)
 * - Real-time data aggregation based on selected filters
 * - Responsive design with dark/light theme support
 * - Multi-language support (English/Spanish)
 * 
 * Architecture:
 * - Single-page application with component-based navigation
 * - State management using React hooks (useState, useEffect)
 * - Material-UI for consistent design and responsive layout
 * - Axios for API communication with FastAPI backend
 * - Custom translation system for internationalization
 * 
 * Data Flow:
 * 1. User selects demographic filters in the UI
 * 2. App.js manages filter state and passes to components
 * 3. Components make API calls to backend with current filters
 * 4. Backend returns filtered/aggregated data
 * 5. Components render visualizations with the filtered data
 * 
 * Main Components:
 * - TrumpApprovalMap: Interactive US map visualization
 * - DemographicFilters: Filter controls for demographic selection
 * - DataTable: Tabular view of demographic data
 * - StatsSummary: Key statistics and metrics
 * - Heatmap: Survey response visualization
 * - RawDataVisualization: Charts for raw polling data analysis
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  Analytics,
  Map,
  Menu as MenuIcon,
  ChevronLeft,
  Settings as SettingsIcon,
  BarChart as BarChartIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocationOn as LocationOnIcon,
  ThumbDown as ThumbDownIcon,
  Help as HelpIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import TrumpApprovalMap from './components/TrumpApprovalMap';
import DemographicFilters from './components/DemographicFilters';
import DataTable from './components/DataTable';
import StatsSummary from './components/StatsSummary';
import Heatmap from './components/Heatmap';
import Login from './components/Login';
import Settings from './components/Settings';
import RawDataVisualization from './components/RawDataVisualization';
import Home from './components/Home';
import Footer from './components/Footer';
import { useTranslation } from './utils/translations';
import './App.css';

/**
 * Create Material-UI theme with custom branding and dark/light mode support.
 * 
 * Theme Features:
 * - Primary color: Republican red (#D03A22) to match political theme
 * - Secondary color: Blue for contrast and accent elements
 * - Dark/light mode with appropriate background colors
 * - Custom font stack for professional appearance
 * - Responsive typography and spacing
 * 
 * @param {string} mode - 'light' or 'dark' theme mode
 * @returns {Theme} Material-UI theme object
 */
const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#D03A22',        // Republican red - main brand color
      light: '#E85A42',       // Lighter shade for hover states
      dark: '#A12E1A',        // Darker shade for pressed states
      contrastText: '#FFFFFF', // White text on primary color
    },
    secondary: {
      main: '#1976d2',        // Material blue - for secondary actions
      light: '#42a5f5',       // Light blue for hover states
      dark: '#1565c0',        // Dark blue for pressed states
      contrastText: '#FFFFFF', // White text on secondary color
    },
    // Dark mode color overrides
    ...(mode === 'dark' && {
      background: {
        default: '#1a1a1a',    // Very dark background for main areas
        paper: '#373737',      // Dark gray for cards and surfaces
      },
    }),
    // Light mode color overrides
    ...(mode === 'light' && {
      background: {
        default: '#fafafa',    // Light gray background
        paper: '#ffffff',      // Pure white for cards and surfaces
      },
    }),
  },
  typography: {
    // Professional font stack prioritizing modern, readable fonts
    fontFamily: '"Poppins", "Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '"Playfair Display", "Poppins", serif',
      fontWeight: 700,
      letterSpacing: '-0.03em',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Playfair Display", "Poppins", serif', 
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Playfair Display", "Poppins", serif',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      letterSpacing: '-0.01em',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      letterSpacing: '0.01em',
      lineHeight: 1.6,
    },
    subtitle2: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      letterSpacing: '0.01em',
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.01em',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.01em',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.02em',
      textTransform: 'none',
      lineHeight: 1.5,
    },
    caption: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.02em',
      lineHeight: 1.4,
    },
    overline: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.2,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.08)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.12)',
    '0px 16px 32px rgba(0,0,0,0.14)',
    '0px 20px 40px rgba(0,0,0,0.16)',
    '0px 24px 48px rgba(0,0,0,0.18)',
    '0px 32px 64px rgba(0,0,0,0.2)',
    '0px 40px 80px rgba(0,0,0,0.22)',
    '0px 48px 96px rgba(0,0,0,0.24)',
    '0px 56px 112px rgba(0,0,0,0.26)',
    '0px 64px 128px rgba(0,0,0,0.28)',
    '0px 72px 144px rgba(0,0,0,0.3)',
    '0px 80px 160px rgba(0,0,0,0.32)',
    '0px 88px 176px rgba(0,0,0,0.34)',
    '0px 96px 192px rgba(0,0,0,0.36)',
    '0px 104px 208px rgba(0,0,0,0.38)',
    '0px 112px 224px rgba(0,0,0,0.4)',
    '0px 120px 240px rgba(0,0,0,0.42)',
    '0px 128px 256px rgba(0,0,0,0.44)',
    '0px 136px 272px rgba(0,0,0,0.46)',
    '0px 144px 288px rgba(0,0,0,0.48)',
    '0px 152px 304px rgba(0,0,0,0.5)',
  ],
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          backgroundColor: mode === 'dark' ? '#404040' : '#404040',
          '&.MuiAppBar-colorInherit': {
            backgroundColor: mode === 'dark' ? '#404040' : '#404040',
          },
          '&.MuiAppBar-colorPrimary': {
            backgroundColor: mode === 'dark' ? '#404040' : '#404040',
          },
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          backgroundColor: mode === 'dark' ? '#414141' : '#F5F5F5',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 24,
          paddingRight: 24,
          fontSize: '0.95rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0px 2px 8px rgba(208, 58, 34, 0.15)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 4px 16px rgba(208, 58, 34, 0.25)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #D03A22 0%, #E85A42 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background: 'linear-gradient(135deg, #A12E1A 0%, #D03A22 100%)',
            boxShadow: '0px 6px 20px rgba(208, 58, 34, 0.3)',
          },
        },
        outlined: {
          borderWidth: 2,
          borderColor: 'rgba(208, 58, 34, 0.3)',
          color: '#D03A22',
          background: 'rgba(208, 58, 34, 0.02)',
          '&:hover': {
            borderColor: '#D03A22',
            background: 'rgba(208, 58, 34, 0.08)',
            borderWidth: 2,
          },
        },
        text: {
          color: '#D03A22',
          '&:hover': {
            background: 'rgba(208, 58, 34, 0.08)',
          },
        },
        sizeSmall: {
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 16,
          paddingRight: 16,
          fontSize: '0.85rem',
        },
        sizeLarge: {
          paddingTop: 16,
          paddingBottom: 16,
          paddingLeft: 32,
          paddingRight: 32,
          fontSize: '1.05rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontFamily: '"Poppins", sans-serif',
            borderRadius: 12,
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '& input': {
              fontWeight: 400,
              letterSpacing: '0.01em',
            },
            '& fieldset': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#D03A22',
              borderWidth: 2,
            },
            '&.Mui-focused': {
              backgroundColor: mode === 'dark' ? 'rgba(208, 58, 34, 0.05)' : 'rgba(208, 58, 34, 0.02)',
              boxShadow: '0px 0px 0px 4px rgba(208, 58, 34, 0.1)',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 400,
          letterSpacing: '0.01em',
          borderRadius: 12,
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D03A22',
            borderWidth: 2,
          },
          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-focused': {
            backgroundColor: mode === 'dark' ? 'rgba(208, 58, 34, 0.05)' : 'rgba(208, 58, 34, 0.02)',
            boxShadow: '0px 0px 0px 4px rgba(208, 58, 34, 0.1)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 500,
            fontSize: '0.95rem',
            letterSpacing: '0.01em',
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
            '&.Mui-focused': {
              color: '#D03A22',
              fontWeight: 600,
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
          borderRadius: 8,
          margin: '2px 8px',
          paddingTop: 12,
          paddingBottom: 12,
          fontSize: '0.95rem',
          fontWeight: 400,
          letterSpacing: '0.01em',
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(208, 58, 34, 0.08)',
            transform: 'translateX(2px)',
            fontWeight: 500,
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(208, 58, 34, 0.12)',
            color: '#D03A22',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(208, 58, 34, 0.16)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          fontSize: '0.85rem',
          height: 32,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
        outlined: {
          borderWidth: 2,
          borderColor: 'rgba(208, 58, 34, 0.3)',
          color: '#D03A22',
          '&:hover': {
            borderColor: '#D03A22',
            backgroundColor: 'rgba(208, 58, 34, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0px 24px 48px rgba(0, 0, 0, 0.2), 0px 12px 24px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
          '&.Mui-checked': {
            color: '#D03A22',
          },
          '&:hover': {
            backgroundColor: 'rgba(208, 58, 34, 0.08)',
          },
        },
      },
    },
  },
});

// === CONFIGURATION CONSTANTS ===
// API base URL - uses environment variable or defaults to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// Sidebar width for consistent layout across components
const drawerWidth = 280;

/**
 * Main App Component
 * 
 * This is the root component that manages the entire application state and layout.
 * It handles authentication, navigation, theme management, and filter state coordination.
 * 
 * State Management:
 * - Authentication: User login status and profile
 * - Navigation: Active tab and sidebar visibility
 * - UI Settings: Theme mode (dark/light) and language preferences
 * - Filters: Demographic filters shared across all visualization components
 * - Data: Loading states and error handling for API calls
 * 
 * Key Responsibilities:
 * 1. Coordinate filter state between components
 * 2. Manage responsive layout and navigation
 * 3. Handle theme and language switching
 * 4. Provide consistent error handling and loading states
 * 5. Orchestrate data flow between backend API and components
 */
function App() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // === AUTHENTICATION STATE ===
  // Currently implemented as placeholder - can be extended for real authentication
  const [user, setUser] = useState(null);                    // User profile information
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Login status
  const [anchorEl, setAnchorEl] = useState(null);            // Profile menu anchor
  
  // === UI LAYOUT STATE ===
  // Manages responsive sidebar behavior and navigation
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile); // Sidebar visibility (closed on mobile by default)
  const [activeTab, setActiveTab] = useState(0);             // Currently active navigation tab/component
  
  // === USER PREFERENCES STATE ===
  // Persisted settings for theme and language
  const [settingsOpen, setSettingsOpen] = useState(false);   // Settings dialog visibility
  const [themeMode, setThemeMode] = useState('light');       // 'light' or 'dark' theme mode
  const [language, setLanguage] = useState('english');       // 'english' or 'spanish' language
  
  // === TRANSLATION SYSTEM ===
  // Custom translation hook for multi-language support
  const t = useTranslation(language);

  // === DATA STATE MANAGEMENT ===
  // Core application data and user interactions
  const [stateData, setStateData] = useState([]);           // State-level approval data for map visualization
  const [selectedState, setSelectedState] = useState(null); // Currently selected state for detailed view
  
  // === FILTER SYSTEM STATE ===
  // Manages the comprehensive demographic filtering system
  
  // Available filter options loaded from backend API
  // These populate the dropdown menus in the filter UI
  const [filterOptions, setFilterOptions] = useState({
    age_groups: [],          // Available age brackets (e.g., ["10-19", "20-29", "30-39", "40-49", "50+"])
    race_groups: [],         // Available race/ethnicity options (e.g., ["Asian", "Black", "Hispanic", "White"])
    party_groups: [],        // Available political parties (e.g., ["Democrat", "Independent", "Republican"])
    gender_groups: [],       // Available gender categories (e.g., ["Female", "Male"])
    education_groups: [],    // Available education levels (e.g., ["2.0", "3.0", "4.0", "5.0"])
    income_groups: [],       // Available income brackets (e.g., ["High", "Low", "Medium"])
    density_groups: []       // Available population density categories (e.g., ["Rural", "Suburban", "Urban"])
  });
  
  // Current filter selections - shared across all visualization components
  // When these change, all components re-fetch data with the new filters
  const [filters, setFilters] = useState({
    age_group: '',           // Selected age group filter (empty string = no filter)
    race_group: '',          // Selected race/ethnicity filter
    party_group: '',         // Selected political party filter
    gender_group: '',        // Selected gender filter
    education_group: '',     // Selected education level filter
    income_group: '',        // Selected income bracket filter
    density_group: ''        // Selected population density filter
  });
  
  // === UI STATE MANAGEMENT ===
  // Loading states and error handling for better user experience
  const [loading, setLoading] = useState(false);            // Global loading state for API calls
  const [error, setError] = useState('');                   // Error message display
  const [fullscreenMapOpen, setFullscreenMapOpen] = useState(false); // Fullscreen map modal state

  // Check for stored auth and theme on component mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem('user');
      }
    }
    
    // Load theme preference
    const storedTheme = localStorage.getItem('themeMode');
    if (storedTheme) {
      setThemeMode(storedTheme);
    }
    
    // Load language preference
    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage) {
      setLanguage(storedLanguage);
    }
  }, []);

  // Close sidebar on mobile when screen size changes
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated && activeTab === 1) {
      fetchFilterOptions();
      fetchStateData();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch data when filters change
  useEffect(() => {
    if (isAuthenticated && activeTab === 1) {
      fetchStateData();
    }
  }, [filters, isAuthenticated, activeTab]);

  // Get actual theme mode (handle system preference)
  const getActualThemeMode = () => {
    if (themeMode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return themeMode;
  };

  // Create theme based on current mode
  const theme = createAppTheme(getActualThemeMode());

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        // Force re-render when system theme changes
        setThemeMode('system');
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  // === AUTHENTICATION HANDLERS ===
  
  /**
   * Handle user login - sets authentication state and persists to localStorage
   * @param {Object} userData - User profile information from login component
   */
  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  /**
   * Handle user logout - clears all authentication state and app data
   * This ensures no sensitive data remains after logout
   */
  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    setAnchorEl(null);
    
    // Clear all application state for security
    setStateData([]);
    setSelectedState(null);
    setFilters({
      age_group: '',
      race_group: '',
      party_group: '',
      gender_group: '',
      education_group: '',
      income_group: '',
      density_group: ''
    });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
    setAnchorEl(null);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleThemeModeChange = (newMode) => {
    setThemeMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    setSelectedState(null); // Clear selected state when switching tabs
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // === DATA FETCHING FUNCTIONS ===
  
  /**
   * Fetch available filter options from the backend API
   * This populates the dropdown menus in the filter UI
   * Called once on component mount to get all possible filter values
   */
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/filter-options`);
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      // Filter options are not critical - app can function without them
    }
  };

  /**
   * Fetch state-level approval data with current demographic filters applied
   * This is the core data fetching function that powers the map visualization
   * 
   * Process:
   * 1. Build URL parameters from current filter selections
   * 2. Call backend /api/states/aggregated endpoint with filters
   * 3. Backend returns state-level data aggregated from matching demographic segments
   * 4. Update stateData which triggers map re-render with new data
   * 
   * Called whenever filters change or component mounts
   */
  const fetchStateData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Build query parameters from current filter selections
      // Only include filters that have values (empty string = no filter)
      const params = new URLSearchParams();
      if (filters.age_group) params.append('age_group', filters.age_group);
      if (filters.race_group) params.append('race_group', filters.race_group);
      if (filters.party_group) params.append('party_group', filters.party_group);
      if (filters.gender_group) params.append('gender_group', filters.gender_group);
      if (filters.education_group) params.append('education_group', filters.education_group);
      if (filters.income_group) params.append('income_group', filters.income_group);
      if (filters.density_group) params.append('density_group', filters.density_group);
      
      const response = await axios.get(`${API_BASE_URL}/states/aggregated?${params}`);
      setStateData(response.data);
    } catch (err) {
      setError('Failed to fetch state data. Please try again.');
      console.error('Error fetching state data:', err);
    } finally {
      setLoading(false);
    }
  };

  // === FILTER MANAGEMENT FUNCTIONS ===
  
  /**
   * Handle changes to demographic filters
   * This is the central filter coordination function - when filters change here,
   * all visualization components automatically re-fetch data with the new filters
   * 
   * @param {Object} newFilters - Updated filter object with new selections
   */
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Note: useEffect hook will automatically call fetchStateData when filters change
  };

  /**
   * Clear all demographic filters and reset to default state
   * This allows users to quickly return to viewing unfiltered data
   */
  const handleClearFilters = () => {
    setFilters({
      age_group: '',
      race_group: '',
      party_group: '',
      gender_group: '',
      education_group: '',
      income_group: '',
      density_group: ''
    });
  };

  // === MAP INTERACTION HANDLERS ===
  
  /**
   * Handle state selection on the map
   * Clicking a state selects it for detailed view; clicking again deselects
   * 
   * @param {string} state - 2-letter state code (e.g., "CA", "TX")
   */
  const handleStateClick = (state) => {
    setSelectedState(state === selectedState ? null : state);
  };

  const handleFullscreenMapOpen = () => {
    setFullscreenMapOpen(true);
  };

  const handleFullscreenMapClose = () => {
    setFullscreenMapOpen(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={handleLogin} language={language} />
        <Footer language={language} />
      </ThemeProvider>
    );
  }

  const menuItems = [
    { 
      text: t('jobApprovalTab'), 
      icon: <Analytics />, 
      value: 1 
    },
    { 
      text: t('surveyResponseHeatmap'), 
      icon: <Map />, 
      value: 2 
    },
    { 
      text: t('rawDataVisualization'), 
      icon: <BarChartIcon />, 
      value: 3 
    }
  ];

  const homeMenuItem = { 
    text: t('home'), 
    icon: <LocationOnIcon />, 
    value: 0 
  };

  const renderSidebar = () => (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      open={sidebarOpen}
      onClose={handleSidebarToggle}
      sx={{
        width: sidebarOpen ? drawerWidth : 0,
        flexShrink: 0,
        transition: muiTheme.transitions.create('width', {
          easing: muiTheme.transitions.easing.sharp,
          duration: muiTheme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          mt: '64px', // Account for AppBar height
          height: 'calc(100vh - 96px)', // Account for AppBar height + footer space
          backgroundColor: getActualThemeMode() === 'dark' ? '#2D2D2D' : '#F5F5F5',
          borderRight: getActualThemeMode() === 'dark' ? '1px solid #373737' : '1px solid #e0e0e0',
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          transition: muiTheme.transitions.create('transform', {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.enteringScreen,
          }),
          transform: sidebarOpen ? 'translateX(0)' : `translateX(-${drawerWidth}px)`,
        },
      }}
    >

      
      <List>
        {menuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              selected={activeTab === item.value}
              onClick={() => handleTabChange(item.value)}
              sx={{
                            '&:hover': {
              backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#e8e8e8',
            },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                '& .MuiListItemIcon-root': {
                  color: getActualThemeMode() === 'dark' ? '#e0e0e0' : '#666666',
                },
                '& .MuiListItemText-primary': {
                  color: getActualThemeMode() === 'dark' ? '#e0e0e0' : '#333333',
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: activeTab === item.value ? 600 : 400
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {/* Spacer to push Home tab to bottom */}
      <Box sx={{ flexGrow: 1 }} />
      
      {/* Home tab at bottom */}
      <List sx={{ pb: 2 }}>
        <Divider sx={{ backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#d0d0d0', my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton
            selected={activeTab === homeMenuItem.value}
            onClick={() => handleTabChange(homeMenuItem.value)}
            sx={{
              '&:hover': {
                backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#e8e8e8',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              '& .MuiListItemIcon-root': {
                color: getActualThemeMode() === 'dark' ? '#e0e0e0' : '#666666',
              },
              '& .MuiListItemText-primary': {
                color: getActualThemeMode() === 'dark' ? '#e0e0e0' : '#333333',
              },
            }}
          >
            <ListItemIcon>
              {homeMenuItem.icon}
            </ListItemIcon>
            <ListItemText 
              primary={homeMenuItem.text}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: activeTab === homeMenuItem.value ? 600 : 400
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );

  const renderMainDashboard = () => (
    <Container maxWidth={false} sx={{ mb: 5, px: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('trumpApprovalAnalysis')}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Filter Controls */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 3,
            backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#ffffff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 1 }} />
                {t('demographicFilters')}
              </Typography>
              
              {Object.values(filters).some(v => v) && (
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  size="small"
                  color="primary"
                >
                  {t('clearAllFilters')}
                </Button>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('ageGroup')}</InputLabel>
                  <Select
                    value={filters.age_group}
                    onChange={(e) => handleFilterChange({ ...filters, age_group: e.target.value })}
                    label={t('ageGroup')}
                  >
                    <MenuItem value="">{t('allAges')}</MenuItem>
                    {filterOptions.age_groups.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('raceEthnicity')}</InputLabel>
                  <Select
                    value={filters.race_group}
                    onChange={(e) => handleFilterChange({ ...filters, race_group: e.target.value })}
                    label={t('raceEthnicity')}
                  >
                    <MenuItem value="">{t('allRaces')}</MenuItem>
                    {filterOptions.race_groups.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('politicalParty')}</InputLabel>
                  <Select
                    value={filters.party_group}
                    onChange={(e) => handleFilterChange({ ...filters, party_group: e.target.value })}
                    label={t('politicalParty')}
                  >
                    <MenuItem value="">{t('allParties')}</MenuItem>
                    {filterOptions.party_groups.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('gender')}</InputLabel>
                  <Select
                    value={filters.gender_group}
                    onChange={(e) => handleFilterChange({ ...filters, gender_group: e.target.value })}
                    label={t('gender')}
                  >
                    <MenuItem value="">{t('allGenders')}</MenuItem>
                    {filterOptions.gender_groups.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('education')}</InputLabel>
                  <Select
                    value={filters.education_group}
                    onChange={(e) => handleFilterChange({ ...filters, education_group: e.target.value })}
                    label={t('education')}
                  >
                    <MenuItem value="">{t('allEducation')}</MenuItem>
                    {filterOptions.education_groups.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('income')}</InputLabel>
                  <Select
                    value={filters.income_group}
                    onChange={(e) => handleFilterChange({ ...filters, income_group: e.target.value })}
                    label={t('income')}
                  >
                    <MenuItem value="">{t('allIncome')}</MenuItem>
                    {filterOptions.income_groups.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('density')}</InputLabel>
                  <Select
                    value={filters.density_group}
                    onChange={(e) => handleFilterChange({ ...filters, density_group: e.target.value })}
                    label={t('density')}
                  >
                    <MenuItem value="">{t('allDensity')}</MenuItem>
                    {filterOptions.density_groups.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Active Filters Display */}
            {Object.values(filters).some(v => v) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('activeFilters')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(filters)
                    .filter(([key, value]) => value !== '' && value !== null)
                    .map(([key, value], index) => {
                      const labels = {
                        age_group: t('age'),
                        race_group: t('race'),
                        party_group: t('party'),
                        gender_group: t('gender'),
                        education_group: t('education'),
                        income_group: t('income'),
                        density_group: t('density')
                      };
                      return (
                        <Chip
                          key={index}
                          label={`${labels[key]}: ${t(value)}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          onDelete={() => handleFilterChange({ ...filters, [key]: '' })}
                          sx={{
                            '& .MuiChip-deleteIcon': {
                              opacity: 0,
                              transition: 'opacity 0.2s ease-in-out',
                            },
                            '&:hover .MuiChip-deleteIcon': {
                              opacity: 1,
                            }
                          }}
                        />
                      );
                    })}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Statistics Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('averageApproval')}</Typography>
                  </Box>
                  <Typography variant="h4" color="primary" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
                    {stateData.length > 0 ? ((stateData.reduce((sum, d) => sum + d.state_TrumpJA, 0) / stateData.length) * 100).toFixed(1) + '%' : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ThumbDownIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('averageDisapproval')}</Typography>
                  </Box>
                  <Typography variant="h4" color="secondary" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
                    {stateData.length > 0 ? ((stateData.reduce((sum, d) => sum + (d.state_TrumpDisapproval || 0), 0) / stateData.length) * 100).toFixed(1) + '%' : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <HelpIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('averageNoOpinion')}</Typography>
                  </Box>
                  <Typography variant="h4" color="info" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
                    {stateData.length > 0 ? ((stateData.reduce((sum, d) => sum + (d.state_TrumpNoOpinion || 0), 0) / stateData.length) * 100).toFixed(1) + '%' : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Grid>

        {/* Map and Data Table Combined */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 2,
            backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#ffffff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ textAlign: 'center', flexGrow: 1 }}>
                {t('trumpApprovalByState')}
              </Typography>
              <IconButton
                onClick={handleFullscreenMapOpen}
                color="primary"
                sx={{ ml: 2 }}
                title="View Fullscreen"
              >
                <FullscreenIcon />
              </IconButton>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
                    <CircularProgress size={60} />
                  </Box>
                                 ) : stateData && stateData.length > 0 ? (
                   <Box sx={{ 
                     height: '500px', 
                     width: '100%', 
                     position: 'relative',
                     overflow: 'hidden',
                     '& .js-plotly-plot': {
                       width: '100% !important',
                       height: '500px !important',
                     }
                   }}>
                     <TrumpApprovalMap 
                       stateData={stateData} 
                       onStateClick={handleStateClick}
                       selectedState={selectedState}
                       sidebarOpen={sidebarOpen}
                       language={language}
                     />
                   </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '500px',
                    flexDirection: 'column'
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      {t('noDataAvailable')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Try adjusting your filter selections
                    </Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper sx={{ 
                  height: '500px', 
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: getActualThemeMode() === 'dark' ? '#373737' : '#ffffff',
                  p: 2
                }}>
                  <DataTable 
                    stateData={stateData} 
                    loading={loading}
                    selectedState={selectedState}
                    onStateClick={handleStateClick}
                    language={language}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );

  const mainContent = (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        width: '100%',
        transition: muiTheme.transitions.create(['width'], {
          easing: muiTheme.transitions.easing.sharp,
          duration: muiTheme.transitions.duration.enteringScreen,
        }),
        pt: '80px',
        pb: '32px', // Account for fixed footer height + some padding
        minHeight: '100vh',
      }}
    >
      {activeTab === 0 && <Home language={language} onTabChange={handleTabChange} />}
      {activeTab === 1 && renderMainDashboard()}
      {activeTab === 2 && <Heatmap sidebarOpen={sidebarOpen} language={language} />}
      {activeTab === 3 && <RawDataVisualization sidebarOpen={sidebarOpen} language={language} />}
    </Box>
  );

  // Show main dashboard if authenticated
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <AppBar position="fixed" color="primary" elevation={0} 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1, 
        }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleSidebarToggle}
              edge="start"
              sx={{ mr: 2 }}
            >
              {sidebarOpen ? <ChevronLeft /> : <MenuIcon />}
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <img
                src="/logos/logo_dark_mode.png"
                alt="Precision Politics Header"
                onClick={() => handleTabChange(0)}
                style={{
                  height: '50px',
                  width: 'auto',
                  marginRight: '12px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                {t('welcome')}, {user?.username}
              </Typography>
              <IconButton
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleSettingsOpen}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  {t('settings')}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ExitToApp sx={{ mr: 1 }} />
                  {t('logout')}
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {renderSidebar()}
        {mainContent}
        
        <Settings
          open={settingsOpen}
          onClose={handleSettingsClose}
          themeMode={themeMode}
          onThemeModeChange={handleThemeModeChange}
          language={language}
          onLanguageChange={handleLanguageChange}
        />
        <Footer language={language} />

        {/* Fullscreen Map Modal */}
        <Dialog
          open={fullscreenMapOpen}
          onClose={handleFullscreenMapClose}
          maxWidth={false}
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              width: '95vw',
              height: '95vh',
              maxWidth: 'none',
              maxHeight: 'none',
              margin: '2.5vh 2.5vw',
              borderRadius: 2,
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1
          }}>
            <Typography variant="h5">
              {t('trumpApprovalByState')}
            </Typography>
            <IconButton
              onClick={handleFullscreenMapClose}
              sx={{ ml: 2 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Fullscreen Map - No Filters */}
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              '& .js-plotly-plot': {
                width: '100% !important',
                height: '100% !important',
              }
            }}>
              {loading ? (
                <CircularProgress size={60} />
              ) : stateData && stateData.length > 0 ? (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <TrumpApprovalMap 
                    stateData={stateData} 
                    onStateClick={handleStateClick}
                    selectedState={selectedState}
                    sidebarOpen={false}
                    language={language}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <Typography variant="h6" color="text.secondary">
                    {t('noDataAvailable')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try adjusting your filter selections
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default App;
